import React, { useState } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Input,
    Label,
    Button,
    Table,
    CardHeader,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const ExpenseReport = () => {
    // State for selected date range and search query
    const [selectedDateRange, setSelectedDateRange] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    // State to store filtered expense reports
    const [filteredExpenseReports, setFilteredExpenseReports] = useState([]);

    // Dummy data for expense reports
    const expenseReports = [
        {
            siNo: 1,
            name: "John Doe",
            invoiceNumber: "EXP001",
            expenseHead: "Salaries",
            date: "2025-01-01",
            amount: 50000,
        },
        {
            siNo: 2,
            name: "Jane Smith",
            invoiceNumber: "EXP002",
            expenseHead: "Utilities",
            date: "2025-01-05",
            amount: 10000,
        },
        {
            siNo: 3,
            name: "Alice Johnson",
            invoiceNumber: "EXP003",
            expenseHead: "Maintenance",
            date: "2025-01-10",
            amount: 15000,
        },
        {
            siNo: 4,
            name: "Bob Brown",
            invoiceNumber: "EXP004",
            expenseHead: "Office Supplies",
            date: "2025-01-25",
            amount: 5000,
        },
        {
            siNo: 5,
            name: "Charlie Davis",
            invoiceNumber: "EXP005",
            expenseHead: "Travel",
            date: "2025-02-15",
            amount: 20000,
        },
        {
            siNo: 6,
            name: "Eva Green",
            invoiceNumber: "EXP006",
            expenseHead: "Marketing",
            date: "2025-02-20",
            amount: 25000,
        },
        {
            siNo: 7,
            name: "Frank White",
            invoiceNumber: "EXP007",
            expenseHead: "Rent",
            date: "2025-02-10",
            amount: 30000,
        },
        {
            siNo: 8,
            name: "Grace Black",
            invoiceNumber: "EXP008",
            expenseHead: "Software Licenses",
            date: "2025-02-05",
            amount: 10000,
        },
        {
            siNo: 9,
            name: "Henry Blue",
            invoiceNumber: "EXP009",
            expenseHead: "Training",
            date: "2025-03-15",
            amount: 15000,
        },
        {
            siNo: 10,
            name: "Ivy Yellow",
            invoiceNumber: "EXP010",
            expenseHead: "Miscellaneous",
            date: "2025-03-13",
            amount: 5000,
        },
    ];

    // Handle date range selection
    const handleDateRangeChange = (e) => {
        const range = e.target.value;
        setSelectedDateRange(range);
        filterExpenseReports(range, searchQuery);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        filterExpenseReports(selectedDateRange, query);
    };

    // Filter expense reports based on date range and search query
    const filterExpenseReports = (dateRange, query) => {
        let filtered = expenseReports;

        // Filter by date range
        if (dateRange) {
            const today = new Date();
            const startDate = new Date();

            switch (dateRange) {
                case "today":
                    startDate.setHours(0, 0, 0, 0);
                    break;
                case "thisWeek":
                    startDate.setDate(today.getDate() - today.getDay());
                    break;
                case "lastWeek":
                    startDate.setDate(today.getDate() - today.getDay() - 7);
                    break;
                case "thisMonth":
                    startDate.setDate(1);
                    break;
                case "lastMonth":
                    startDate.setMonth(today.getMonth() - 1, 1);
                    break;
                case "3Months":
                    startDate.setMonth(today.getMonth() - 3);
                    break;
                case "6Months":
                    startDate.setMonth(today.getMonth() - 6);
                    break;
                case "12Months":
                    startDate.setMonth(today.getMonth() - 12);
                    break;
                case "1Year":
                    startDate.setFullYear(today.getFullYear() - 1);
                    break;
                default:
                    break;
            }

            filtered = filtered.filter((report) => {
                const reportDate = new Date(report.date);
                return reportDate >= startDate;
            });
        }

        // Filter by search query
        if (query) {
            filtered = filtered.filter(
                (report) =>
                    report.name.toLowerCase().includes(query.toLowerCase()) ||
                    report.invoiceNumber.toLowerCase().includes(query.toLowerCase()) ||
                    report.expenseHead.toLowerCase().includes(query.toLowerCase())
            );
        }

        setFilteredExpenseReports(filtered);
    };

    // Calculate grand total
    const grandTotal = filteredExpenseReports.reduce((total, report) => total + report.amount, 0);

    // Breadcrumb items
    const breadcrumbItems = [
        { title: "Reports", link: "/" },
        { title: "Finance Reports", link: "/" },
        { title: "Expense Report", link: "/admin/expense-report" },
    ];

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Expense Report" breadcrumbItems={breadcrumbItems} />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
                                    <h4 className="card-title mb-0 flex-grow-1">Expense Report</h4>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        {/* Date Range Select Input */}
                                        <Col lg={3}>
                                            <Label>Date Range</Label>
                                            <Input
                                                type="select"
                                                value={selectedDateRange}
                                                onChange={handleDateRangeChange}
                                            >
                                                <option value="">Select Date Range</option>
                                                <option value="today">Today</option>
                                                <option value="thisWeek">This Week</option>
                                                <option value="lastWeek">Last Week</option>
                                                <option value="thisMonth">This Month</option>
                                                <option value="lastMonth">Last Month</option>
                                                <option value="3Months">Last 3 Months</option>
                                                <option value="6Months">Last 6 Months</option>
                                                <option value="12Months">Last 12 Months</option>
                                                <option value="1Year">Last 1 Year</option>
                                            </Input>
                                        </Col>

                                        {/* Search Input */}
                                        <Col lg={6}>
                                            <Label>Search</Label>
                                            <Input
                                                type="text"
                                                placeholder="Search by Name, Invoice Number, or Expense Head"
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                            />
                                        </Col>

                                        {/* Search Button */}
                                        <Col lg={3} className="d-flex align-items-end">
                                            <Button color="primary" onClick={() => filterExpenseReports(selectedDateRange, searchQuery)}>
                                                Search
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* Expense Report Table */}
                                    <Row className="mt-4">
                                        <Col lg={12}>
                                            {filteredExpenseReports.length > 0 ? (
                                                <Table striped>
                                                    <thead>
                                                        <tr>
                                                            <th>SI No</th>
                                                            <th>Name</th>
                                                            <th>Invoice Number</th>
                                                            <th>Expense Head</th>
                                                            <th>Date</th>
                                                            <th>Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredExpenseReports.map((report, index) => (
                                                            <tr key={report.siNo}>
                                                                {/* Dynamically assign SI No based on the filtered list index */}
                                                                <td>{index + 1}</td>
                                                                <td>{report.name}</td>
                                                                <td>{report.invoiceNumber}</td>
                                                                <td>{report.expenseHead}</td>
                                                                <td>{report.date}</td>
                                                                <td>{report.amount}</td>
                                                            </tr>
                                                        ))}
                                                        {/* Grand Totals Row */}
                                                        <tr style={{ fontWeight: "bold" }}>
                                                            <td colSpan="5">Grand Total</td>
                                                            <td>{grandTotal}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            ) : (
                                                <div className="text-center">
                                                    <p>No Record Found</p>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ExpenseReport;